import { useCallback, useEffect, useState } from "react";
import { Code2, Copy, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loading, Panel } from "@/components/common/Chrome";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const TEXT_FIELDS = [
  ["headline", "Headline", "input"],
  ["intro", "Welcome paragraph", "area"],
  ["story_title", "Impact section title", "input"],
  ["story_body", "Impact story", "area"],
  ["impact_statement", "Closing statement", "area"],
  ["donate_note", "Give-again note", "area"],
  ["contact_email", "Contact address shown to donors", "input"],
];

const TOGGLES = [
  ["show_donate_cta", "Show the “Give again” tab"],
  ["show_projects", "Show ongoing and future projects"],
  ["show_volunteer_form", "Show the volunteer sign-up form"],
];

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

export const DonorPortalSettings = () => {
  const [meta, setMeta] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState("");
  const portalUrl = `${window.location.origin}/donor/signin`;
  const snippet = `<a href="${portalUrl}" onclick="window.open(this.href,'donor','width=460,height=620');return false">Donor login</a>`;

  const load = useCallback(async () => {
    const res = await api.get("/donor/portal-config/admin");
    setMeta(res.data);
    setValues(res.data.values);
  }, []);

  useEffect(() => { load().catch(() => setMeta(false)); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/donor/portal-config/admin", { values });
      toast.success("Donor portal updated");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const setStat = (i, key, v) => {
    const stats = [...(values.impact_stats || [])];
    stats[i] = { ...stats[i], [key]: v };
    setValues({ ...values, impact_stats: stats });
  };

  if (!meta) return <Loading label="Loading donor portal settings" />;
  const dirty = JSON.stringify(values) !== JSON.stringify(meta.values);

  return (
    <div className="space-y-4">
      <Panel testId="panel-portal-link" kicker="Embed" title="Put the donor login on your own website">
        <div className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 truncate rounded-lg bg-muted/50 px-3 py-2 font-mono text-[11px] text-foreground"
                  data-testid="portal-link">{portalUrl}</code>
            <Button size="sm" variant="outline" data-testid="portal-copy-link"
                    onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link copied"); }}
                    className="h-8 rounded-full border-border text-[11px]">
              <Copy className="mr-1 h-3.5 w-3.5" /> Copy link
            </Button>
            <Button size="sm" variant="outline" data-testid="portal-copy-snippet"
                    onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Snippet copied"); }}
                    className="h-8 rounded-full border-border text-[11px]">
              <Code2 className="mr-1 h-3.5 w-3.5" /> Copy embed snippet
            </Button>
            <a href={portalUrl} target="donor-preview" data-testid="portal-preview-link"
               onClick={(e) => { e.preventDefault(); window.open(portalUrl, "donor", "width=460,height=640"); }}
               className="inline-flex h-8 items-center rounded-full border border-primary/40 px-3 text-[11px] text-primary transition-colors duration-150 hover:bg-primary/10">
              Preview the popup
            </a>
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Paste the snippet anywhere on your own site. It opens a small sign-in window headed with
            your charity's name and logo, so donors always know whose account they are entering.
          </p>
        </div>
      </Panel>

      <Panel testId="panel-portal-content" kicker="Tenant Admin" title="What your donors see"
             actions={meta.editable && (
               <div className="flex gap-2">
                 <Button size="sm" variant="outline" onClick={() => setValues(meta.defaults)}
                         data-testid="portal-reset-button" className="h-8 rounded-full border-border text-[11px]">
                   <RotateCcw className="mr-1 h-3.5 w-3.5" /> Defaults
                 </Button>
                 <Button size="sm" onClick={save} disabled={busy === "save" || !dirty}
                         data-testid="portal-save-button"
                         className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                   {busy === "save" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                     : <Save className="mr-1 h-3.5 w-3.5" />} Save portal
                 </Button>
               </div>
             )}>
        <div className="space-y-4 p-4">
          {TEXT_FIELDS.map(([key, label, kind]) => (
            <F key={key} label={label}>
              {kind === "area"
                ? <Textarea rows={2} value={values[key] || ""} disabled={!meta.editable}
                            data-testid={`portal-${key}-input`}
                            onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                            className="bg-muted/40 text-[12px]" />
                : <Input value={values[key] || ""} disabled={!meta.editable}
                         data-testid={`portal-${key}-input`}
                         onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                         className="bg-muted/40 text-[12.5px]" />}
            </F>
          ))}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="How donors get an account">
              <Select value={values.signup_mode || "approval"} disabled={!meta.editable}
                      onValueChange={(v) => setValues({ ...values, signup_mode: v })}>
                <SelectTrigger data-testid="portal-signup_mode-input" className="bg-muted/40">
                  <SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open — they set a password straight away</SelectItem>
                  <SelectItem value="approval">Fundraising approves each new account</SelectItem>
                  <SelectItem value="invite_only">Invitation only — self sign-up is off</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="What they read on the sign-up form">
              <Input value={values.signup_note || ""} disabled={!meta.editable}
                     data-testid="portal-signup_note-input"
                     onChange={(e) => setValues({ ...values, signup_note: e.target.value })}
                     className="bg-muted/40 text-[12.5px]" />
            </F>
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] text-muted-foreground">Impact figures shown to donors</Label>
            {(values.impact_stats || []).map((st, i) => (
              <div key={i} className="grid grid-cols-[1fr_150px] gap-2">
                <Input value={st.label} disabled={!meta.editable} placeholder="What it measures"
                       data-testid={`portal-stat-label-${i}`}
                       onChange={(e) => setStat(i, "label", e.target.value)}
                       className="bg-muted/40 text-[12px]" />
                <Input value={st.value} disabled={!meta.editable} placeholder="Figure"
                       data-testid={`portal-stat-value-${i}`}
                       onChange={(e) => setStat(i, "value", e.target.value)}
                       className="bg-muted/40 font-mono text-[12px]" />
              </div>
            ))}
          </div>

          <div className="space-y-2.5 rounded-xl border border-border p-3">
            {TOGGLES.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-[12.5px] text-foreground">{label}</span>
                <Switch checked={Boolean(values[key])} disabled={!meta.editable}
                        data-testid={`portal-toggle-${key}`}
                        onCheckedChange={(v) => setValues({ ...values, [key]: v })} />
              </div>
            ))}
          </div>

          <F label="Featured projects (leave empty to show the most recent)">
            <div className="flex flex-wrap gap-2">
              {meta.programmes.map((p) => {
                const on = (values.featured_project_codes || []).includes(p.code);
                return (
                  <button key={p.code} type="button" disabled={!meta.editable}
                          data-testid={`portal-project-${p.code}`}
                          onClick={() => setValues({
                            ...values,
                            featured_project_codes: on
                              ? values.featured_project_codes.filter((c) => c !== p.code)
                              : [...(values.featured_project_codes || []), p.code],
                          })}
                          className={`rounded-full border px-3 py-1.5 text-[11px] transition-colors duration-150 ${
                            on ? "border-primary/50 bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:bg-muted"}`}>
                    {p.code}
                  </button>
                );
              })}
            </div>
          </F>
        </div>
        <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground"
             data-testid="portal-audit-line">
          {meta.updated_by
            ? `Last changed by ${meta.updated_by} · ${dateTime(meta.updated_at)}`
            : "Still on the default copy."}
        </div>
      </Panel>
    </div>
  );
};
